import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useProductOptionsQuery } from "@/modules/options/presentation/api/queries";
import type {
  ProductCreateInput,
  ProductUpdateInput,
} from "@/modules/products/domain/contracts";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { HelpCircleIcon } from "lucide-react";
import { useProductQuery, useUpdateProduct } from "../api/queries";
import { ProductFormTour, useProductFormTour } from "../tour";
import {
  getCatalogModeForKind,
  getProductCatalogConfig,
  isCatalogModeMatch,
  type ProductCatalogMode,
} from "../ui/product-catalog-config";
import { inferProductKind } from "../ui/product-kind";
import { ProductForm, type ProductFormValues } from "../ui/ProductForm";

function nameLo(name: unknown): string {
  if (typeof name === "object" && name !== null && name !== undefined) {
    const o = name as { lo?: string; en?: string; th?: string };
    return o.lo ?? o.en ?? o.th ?? "ແກ້ໄຂ";
  }
  return String(name ?? "ແກ້ໄຂ");
}

type ProductEditPageProps = {
  catalogMode?: ProductCatalogMode;
};

export function ProductEditPage({
  catalogMode = "retail",
}: ProductEditPageProps) {
  const config = getProductCatalogConfig(catalogMode);
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const { data: product, isLoading } = useProductQuery(id ?? "");
  const updateProduct = useUpdateProduct(id ?? "");
  const { run, handleJoyrideCallback, startTour } = useProductFormTour();

  const { data: optionsData } = useProductOptionsQuery({
    limit: 100,
    includeValues: true,
  });
  const optionOptions = optionsData?.data || [];

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: config.labels.breadcrumb, to: config.routes.list },
              { label: id ?? "ແກ້ໄຂ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: config.labels.breadcrumb, to: config.routes.list },
              { label: id ?? "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ບໍ່ພົບສິນຄ້າ</p>
          </div>
        </Main>
      </>
    );
  }

  const isWrongCatalog = !isCatalogModeMatch(catalogMode, product.kind);

  if (isWrongCatalog) {
    const otherConfig = getProductCatalogConfig(
      getCatalogModeForKind(product.kind),
    );
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: config.labels.breadcrumb, to: config.routes.list },
              { label: nameLo(product.name) },
            ]}
          />
        </Header>
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <p className="text-muted-foreground">
              ລາຍການນີ້ຢູ່ໃນເມນູ {otherConfig.labels.breadcrumb}
            </p>
            <Button asChild variant="outline">
              <Link
                to={otherConfig.routes.detail}
                params={{ id: product.id }}
              >
                ໄປທີ່ {otherConfig.labels.breadcrumb}
              </Link>
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const initialValues: Partial<ProductFormValues> = {
    nameLo: (product.name as { lo?: string })?.lo ?? "",
    nameTh: (product.name as { th?: string })?.th ?? "",
    nameEn: (product.name as { en?: string })?.en ?? "",
    slug: product.slug,
    descriptionLo: (product.description as { lo?: string })?.lo ?? "",
    descriptionTh: (product.description as { th?: string })?.th ?? "",
    descriptionEn: (product.description as { en?: string })?.en ?? "",
    status: product.status,
    kind: inferProductKind(
      product.kind ?? "retail",
      product.isSellable ?? true,
      product.isShowcaseEligible ?? false,
    ),
    tags:
      product.tags?.map((t) => ({
        id: t.id,
        name: (t.name as { lo?: string })?.lo ?? t.slug,
        slug: t.slug,
      })) ?? [],
    categoryIds: product.categories?.map((c) => c.id) ?? [],
    collectionIds: product.collections?.map((c) => c.id) ?? [],
    optionIds: product.options?.map((o) => o.id) ?? [],
    optionValueLinks: product.optionValueLinks ?? {},
    showcaseServiceSlugs: product.showcaseServiceSlugs ?? [],
  };

  const handleSubmit = async (
    values: ProductCreateInput | ProductUpdateInput,
  ) => {
    await updateProduct.mutateAsync(values as ProductUpdateInput);
    nav({ to: config.routes.detail, params: { id } });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: config.labels.breadcrumb, to: config.routes.list },
            { label: nameLo(product.name) },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl tracking-tight">
            {config.labels.editTitle}{" "}
            {catalogMode === "retail" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="ml-2 size-6"
                title="ເລີ່ມທົດລອງການນຳທາງ"
              >
                <HelpCircleIcon className="h-4 w-4" />
              </Button>
            )}
          </h1>
          <p className="text-muted-foreground">{config.labels.editDescription}</p>
        </div>

        <ProductForm
          catalogMode={catalogMode}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onCancel={() => nav({ to: config.routes.detail, params: { id } })}
          submitting={updateProduct.isPending}
          optionOptions={optionOptions}
        />
        {catalogMode === "retail" && (
          <ProductFormTour run={run} onCallback={handleJoyrideCallback} />
        )}
      </Main>
    </>
  );
}
